import {
  ProgramRedesignTrafficProps,
  UseProgramRedesignTraffic,
  ProgramRedesignTrafficVariant,
  ProgramRedesignTrafficTestResult,
  UseProgramRedesignConfig,
} from "./types";
import { useMonarchRule } from "../MonarchRulesProvider";
import { PromoBannerMonarchProps } from "@/monarch/types/PromoBanner";

const normalizePromoBanner = (
  banner?: PromoBannerMonarchProps,
): PromoBannerMonarchProps | undefined => {
  if (!banner?.complete?.cta) return banner;
  const { htmlId, ...cta } = banner.complete
    .cta as typeof banner.complete.cta & {
    htmlId?: string;
  };
  return {
    ...banner,
    complete: { ...banner.complete, cta: { ...cta, html_id: htmlId } },
  };
};

const CUSTOM_RULE_ID = "programPageRedesignTest";

const useProgramRedesignTraffic: UseProgramRedesignTraffic = () => {
  const { isLoading, value } = useMonarchRule<{
    data: ProgramRedesignTrafficProps;
    variantCustomId: string;
  }>(CUSTOM_RULE_ID, {});

  // If still loading or no value returned, return loading state
  if (isLoading || !value) {
    return {
      isLoading,
    } as ProgramRedesignTrafficTestResult;
  }
  // Determine the variant based on the returned data
  let variant: ProgramRedesignTrafficVariant = "control";

  // First check if displayExperience is set
  if (value.data.displayExperience) {
    variant = value.data.displayExperience;
  } else if (value.data.showRedesign) {
    // If displayExperience is not set, fall back to showRedesign logic
    variant = "redesign";
  }

  // Extract data for the experiment
  const data: ProgramRedesignTrafficProps = value.data;
  // Return the experiment result with proper typing
  return {
    isLoading: false,
    variant,
    data,
  };
};

// Hook for configuration data access
export const useProgramRedesignConfig: UseProgramRedesignConfig = () => {
  const { isLoading, value } = useMonarchRule<{
    data: ProgramRedesignTrafficProps;
    variantCustomId: string;
  }>(CUSTOM_RULE_ID, {});

  return {
    isLoading,
    showRedesign: value?.data?.showRedesign,
    displayExperience: value?.data?.displayExperience,
    programEnrollCta: value?.data?.programEnrollCta,
    headerCta: value?.data?.headerCta,
    currencySelector: value?.data?.currencySelector,
    multiCurrency: value?.data?.multiCurrency,
    geoPricing: value?.data?.geoPricing,
    simpleMobileHeader: value?.data?.simpleMobileHeader,
    factoidsBar: value?.data?.factoidsBar,
    promoBanner: value?.data?.promoBanner,
    productCompare: value?.data?.productCompare,
    sectionsOrder: value?.data?.sectionsOrder,
    showSideRail: value?.data?.showSideRail,
    sideRailTitle: value?.data?.sideRailTitle,
    sideRailBulletList: value?.data?.sideRailBulletList,
    sideRailStatisticComponent: value?.data?.sideRailStatisticComponent,
    sideRailImage: value?.data?.sideRailImage,
    sideRailImageTitle: value?.data?.sideRailImageTitle,
    sideRailHeaderComponents: value?.data?.sideRailHeaderComponents,
    sideRailBodyComponents: value?.data?.sideRailBodyComponents,
    sideRailCollapsedHeaderComponents:
      value?.data?.sideRailCollapsedHeaderComponents,
    sideRailCollapsedBodyComponents:
      value?.data?.sideRailCollapsedBodyComponents,
    sideRailMobileHeaderComponents: value?.data?.sideRailMobileHeaderComponents,
    sideRailMobileBodyComponents: value?.data?.sideRailMobileBodyComponents,
    showRedesignedTrackComparison: value?.data?.showRedesignedTrackComparison,
    emphasisModuleVersion: value?.data?.emphasisModuleVersion,
    information: value?.data?.information,
    stats: value?.data?.stats,
    testimonial: value?.data?.testimonial,
    tableDescription: value?.data?.tableDescription,
    pathInfo: value?.data?.pathInfo,
    tabsOrder: value?.data?.tabsOrder,
    hidePrice: value?.data?.hidePrice,
    mainContent: value?.data?.mainContent,
    collapsableContent: value?.data?.collapsableContent,
    collapsableContentLabel: value?.data?.collapsableContentLabel,
    stepComponent: value?.data?.stepComponent,
    showHeaderPrice: value?.data?.showHeaderPrice,
    showHeaderPriceMobile: value?.data?.showHeaderPriceMobile,
    verifiedBadge: value?.data?.verifiedBadge,
    whyTakeProgram: value?.data?.whyTakeProgram,
    highlights: value?.data?.highlights,
    alumniTestimonials: value?.data?.alumniTestimonials,
    partnershipDescription: value?.data?.partnershipDescription,
    countdownBanner: value?.data?.countdownBanner,
    sideRailProductTitle: value?.data?.sideRailProductTitle,
    sideRailRating: value?.data?.sideRailRating,
    sideRailVideoPlayer: value?.data?.sideRailVideoPlayer,
    sideRailSkillsList: value?.data?.sideRailSkillsList,
    showHeaderFactoidBar: value?.data?.showHeaderFactoidBar,
    statsBanner: value?.data?.statsBanner,
    bundleSavingsBanner: value?.data?.bundleSavingsBanner,
    benefitsModule: value?.data?.benefitsModule,
    insertBadges: value?.data?.insertBadges,
    showDynamicBadges: value?.data?.showDynamicBadges,
    partnerLogo: value?.data?.partnerLogo,
    showHeaderRibbon: value?.data?.showHeaderRibbon,
    headerRibbonText: value?.data?.headerRibbonText,
    headerBackgroundImageUrl: value?.data?.headerBackgroundImageUrl,
    careerCards: value?.data?.careerCards,
    testimonialsModule: value?.data?.testimonialsModule,
    expandableRelatedPrograms: value?.data?.expandableRelatedPrograms,
  };
};

export default useProgramRedesignTraffic;
